import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import { Calendar, User, Briefcase, FileText, CreditCard, Percent, X, Award, Gift, Sparkles, MessageSquare, Loader2, Package } from 'lucide-react';
import SearchableDropdown from '../../../components/SearchableDropdown';
import MultiServiceSelector from '../../../components/MultiServiceSelector';
import TierBadge from '../../../components/TierBadge';
import { getDependenciesByIds } from '../../../utils/firebaseService';
import { getLoyaltyConfig } from '../../../utils/loyaltyService';
import { useAuth } from '../../../context/AuthContext';
import usePlanFeatures from '../../../hooks/usePlanFeatures';
import {
  normalizeLoyalty,
  calculateTier,
  calculateEarnPoints,
  getTierConfig,
} from '../../../utils/loyaltyCalculations';

const BillModal = ({
  isOpen,
  onClose,
  billForm,
  setBillForm,
  onBillFormChange,
  onSave,
  isEditing,
  applyDiscount,
  setApplyDiscount,
  discountAmount,
  setDiscountAmount,
  selectedServices,
  setSelectedServices,
  customers,
  employees,
  services,
  useLoyaltyPoints = false,
  setUseLoyaltyPoints = () => {},
  pointsToUse = 0,
  setPointsToUse = () => {},
  isSaving = false,
  customerActivePackages = [],
  servicePackageUsage = {},
  setServicePackageUsage = () => {},
}) => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation('home');
  const { isFeatureAllowed } = usePlanFeatures();
  const isTrackingAllowed = isFeatureAllowed('tracking');
  const numLocale = i18n.language === 'en' ? 'en-GB' : 'tr-TR';
  const [serviceDependencies, setServiceDependencies] = useState({});
  const [loadingDependencies, setLoadingDependencies] = useState(false);
  const [loyaltyConfig, setLoyaltyConfig] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    getLoyaltyConfig(user.businessId)
      .then((cfg) => { if (!cancelled) setLoyaltyConfig(cfg); })
      .catch((err) => console.error('Loyalty config fetch error:', err));
    return () => { cancelled = true; };
  }, [isOpen]);

  const customerLoyalty = useMemo(
    () => normalizeLoyalty(billForm.selectedCustomer?.loyalty),
    [billForm.selectedCustomer],
  );

  const packageDiscount = useMemo(() => {
    if (isEditing) return Number(billForm.packageDiscount) || 0;
    if (!servicePackageUsage || Object.keys(servicePackageUsage).length === 0) return 0;
    return selectedServices
      .filter(s => !s._isPackage)
      .reduce((sum, s) => servicePackageUsage[s.id] ? sum + Number(s.price) : sum, 0);
  }, [isEditing, billForm.packageDiscount, selectedServices, servicePackageUsage]);

  const getPackageForService = (serviceId) => {
    if (!customerActivePackages || !serviceId) return null;
    return customerActivePackages.find(cp =>
      cp.status === 'active' &&
      cp.remainingSessions &&
      (cp.remainingSessions[serviceId] || 0) > 0
    ) || null;
  };

  const baseTotal = useMemo(
    () => selectedServices.reduce((sum, s) => sum + Number(s.price), 0),
    [selectedServices],
  );
  const discountValue = applyDiscount ? Number(discountAmount) || 0 : 0;
  const afterDiscount = Math.max(0, baseTotal - discountValue - packageDiscount);

  const redeemRate = Number(loyaltyConfig?.redeemRate) || 1;
  const minRedeem  = Number(loyaltyConfig?.minRedeem)  || 0;
  const maxUsableByPrice = redeemRate > 0 ? Math.floor(afterDiscount / redeemRate) : 0;
  const maxUsablePoints  = Math.max(0, Math.min(customerLoyalty.currentPoints, maxUsableByPrice));
  const canRedeem = Boolean(
    loyaltyConfig && billForm.selectedCustomer &&
    customerLoyalty.currentPoints >= minRedeem && maxUsableByPrice >= minRedeem,
  );
  const effectivePointsToUse = useLoyaltyPoints ? Math.min(Number(pointsToUse) || 0, maxUsablePoints) : 0;
  const loyaltyDiscount = effectivePointsToUse * redeemRate;
  const finalTotal = Math.max(0, afterDiscount - loyaltyDiscount);

  const earnPreview = useMemo(() => {
    if (!loyaltyConfig || !billForm.selectedCustomer) return null;
    const tierNow = calculateTier(customerLoyalty.lifetimePoints, loyaltyConfig);
    const tierCfg = getTierConfig(tierNow, loyaltyConfig);
    const points  = calculateEarnPoints(finalTotal, tierNow, loyaltyConfig);
    return { points, tier: tierNow, multiplier: tierCfg.multiplier };
  }, [loyaltyConfig, billForm.selectedCustomer, customerLoyalty.lifetimePoints, finalTotal]);

  // Modal kapandığında yerel bağımlılık state'ini temizle — bir sonraki açılışta
  // (özellikle randevudan adisyon oluştururken) önceki servislerin tracking
  // alanları sızmasın.
  useEffect(() => {
    if (!isOpen) {
      setServiceDependencies({});
      setLoadingDependencies(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isTrackingAllowed) {
      setServiceDependencies({});
      return;
    }
    let cancelled = false;
    const fetchDependencies = async () => {
      const uniqueServices = {};
      selectedServices.forEach(s => { if (!uniqueServices[s.id]) uniqueServices[s.id] = s; });
      const serviceDepMap = {};
      setLoadingDependencies(true);
      try {
        for (const serviceId of Object.keys(uniqueServices)) {
          const masterService = services.find(ms => ms.id === serviceId);
          if (masterService?.dependenciesId && Array.isArray(masterService.dependenciesId)) {
            const deps = await getDependenciesByIds(masterService.dependenciesId);
            if (deps.length > 0) {
              serviceDepMap[serviceId] = { serviceName: masterService.name, dependencies: deps };
            }
          }
        }
      } catch (err) {
        console.error('Error fetching dependencies:', err);
      } finally {
        // Hata / iptal olsa bile state'i güncelle ki önceki servislerin
        // dependency alanları kalıcı olarak ekranda kalmasın.
        if (!cancelled) {
          setServiceDependencies(serviceDepMap);
          setLoadingDependencies(false);
        }
      }
    };
    fetchDependencies();
    return () => { cancelled = true; };
  }, [isTrackingAllowed, selectedServices, services]);

  const handleTrackingChange = (serviceId, dep, selectedKey) => {
    const contentValue = dep.contents[selectedKey];
    setBillForm(prev => ({
      ...prev,
      trackingInfo: {
        ...prev.trackingInfo,
        [serviceId]: {
          ...(prev.trackingInfo?.[serviceId] || {}),
          dependencies: {
            ...(prev.trackingInfo?.[serviceId]?.dependencies || {}),
            [dep.id]: {
              name: dep.name,
              contentValue,
              selectedKey,
              description: prev.trackingInfo?.[serviceId]?.dependencies?.[dep.id]?.description || '',
            },
          },
        },
      },
    }));
  };

  const handleDescriptionChange = (serviceId, depId, description) => {
    setBillForm(prev => ({
      ...prev,
      trackingInfo: {
        ...prev.trackingInfo,
        [serviceId]: {
          ...(prev.trackingInfo?.[serviceId] || {}),
          dependencies: {
            ...(prev.trackingInfo?.[serviceId]?.dependencies || {}),
            [depId]: {
              ...(prev.trackingInfo?.[serviceId]?.dependencies?.[depId] || {}),
              description,
            },
          },
        },
      },
    }));
  };

  useEffect(() => {
    const totalPrice = selectedServices.reduce((sum, service) => sum + Number(service.price), 0);
    setBillForm(prev => ({ ...prev, price: totalPrice }));
  }, [selectedServices, setBillForm]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={{
        overlay: { backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
        content: { position: 'relative', top: 'auto', left: 'auto', right: 'auto', bottom: 'auto', maxWidth: '600px', width: '90%', maxHeight: '90vh', margin: '0 auto', padding: 0, border: 'none', background: 'transparent', overflow: 'visible' },
      }}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-4 rounded-t-lg flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white">
            {isEditing ? t('billModal.titleEdit') : t('billModal.titleNew')}
          </h2>
          <button onClick={onClose} disabled={isSaving} className="text-white hover:text-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Tarih */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Calendar className="h-4 w-4 text-blue-500" />
              {t('billModal.dateLabel')}
            </label>
            <input type="date" value={billForm.date} onChange={(e) => onBillFormChange('date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />
          </div>

          {/* Müşteri */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <User className="h-4 w-4 text-blue-500" />
              {t('billModal.customerLabel')}
            </label>
            <SearchableDropdown
              items={customers}
              onSelect={(customer) => {
                setBillForm(prev => ({ ...prev, selectedCustomer: customer }));
                onBillFormChange('customerId', customer.id);
              }}
              placeholder={t('billModal.customerPlaceholder')}
              value={billForm.selectedCustomer}
              icon={User}
            />
          </div>

          {/* Çalışan */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Briefcase className="h-4 w-4 text-blue-500" />
              {t('billModal.employeeLabel')}
            </label>
            <SearchableDropdown
              items={employees}
              onSelect={(employee) => setBillForm(prev => ({ ...prev, employeeId: employee.id, selectedEmployee: employee }))}
              placeholder={t('billModal.employeePlaceholder')}
              value={billForm.selectedEmployee}
              icon={Briefcase}
            />
          </div>

          {/* Hizmet */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <FileText className="h-4 w-4 text-blue-500" />
              {t('billModal.serviceLabel')}
            </label>
            <MultiServiceSelector
              services={services}
              selectedServices={selectedServices}
              setSelectedServices={setSelectedServices}
              resetAfterSelect={true}
              maxServices={10}
              maxSessionsPerService={10}
            />

            {/* Paket chip'leri */}
            {billForm.selectedCustomer && customerActivePackages.length > 0 && (() => {
              const uniqueServiceIds = [...new Set(selectedServices.filter(s => !s._isPackage).map(s => s.id))];
              const chipsToShow = uniqueServiceIds
                .map(sid => ({ sid, pkg: getPackageForService(sid) }))
                .filter(({ pkg }) => pkg !== null);
              if (chipsToShow.length === 0) return null;
              return (
                <div className="mt-3 space-y-1.5">
                  {chipsToShow.map(({ sid, pkg }) => {
                    const serviceName = selectedServices.find(s => s.id === sid)?.name || sid;
                    const rem = pkg.remainingSessions?.[sid] || 0;
                    const isUsing = !!servicePackageUsage[sid];
                    return (
                      <div key={sid} className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-blue-800 font-medium">{serviceName}</span>
                          <span className="text-xs text-blue-600">{rem} seans kaldı</span>
                        </div>
                        <button type="button"
                          onClick={() => setServicePackageUsage(prev => {
                            const next = { ...prev };
                            if (isUsing) delete next[sid]; else next[sid] = pkg.id;
                            return next;
                          })}
                          className={`text-xs font-semibold px-2 py-1 rounded-full transition-colors ${isUsing ? 'bg-blue-600 text-white' : 'bg-white border border-blue-300 text-blue-600 hover:bg-blue-100'}`}
                        >
                          {isUsing ? '✓ Paketten kullanılıyor' : 'Paketten kullan'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {/* Tracking — sadece tracking feature'ı olan planlarda gösterilir */}
          {isTrackingAllowed && Object.keys(serviceDependencies).length > 0 && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-blue-800 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {t('billModal.trackingTitle')}
              </h3>
              {Object.entries(serviceDependencies).map(([serviceId, serviceData]) => (
                <div key={serviceId} className="space-y-3 bg-blue-50 p-4 rounded-lg border border-blue-100">
                  <h4 className="text-sm font-semibold text-blue-700">{serviceData.serviceName}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {serviceData.dependencies.map(dep => (
                      <div key={dep.id} className="space-y-2">
                        <label className="text-xs font-medium text-gray-700 block">{dep.name}</label>
                        <select
                          value={billForm.trackingInfo?.[serviceId]?.dependencies?.[dep.id]?.selectedKey || ''}
                          onChange={(e) => handleTrackingChange(serviceId, dep, e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                        >
                          <option value="">{t('billModal.trackingSelectDefault')}</option>
                          {dep.contents && Object.entries(dep.contents).map(([key, value]) => (
                            <option key={key} value={key}>{value}</option>
                          ))}
                        </select>
                        <input type="text"
                          placeholder={t('billModal.trackingDescPlaceholder')}
                          value={billForm.trackingInfo?.[serviceId]?.dependencies?.[dep.id]?.description || ''}
                          onChange={(e) => handleDescriptionChange(serviceId, dep.id, e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white placeholder-gray-400"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Fiyat / İndirim */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-lg font-medium text-gray-900 mb-4 flex items-center justify-between">
              <span>{t('billModal.totalLabel')}</span>
              <span>{baseTotal} TL</span>
            </div>
            {packageDiscount > 0 && (
              <div className="flex items-center justify-between text-sm text-blue-700 mb-2">
                <span className="flex items-center gap-1">Paket indirimi</span>
                <span>−{packageDiscount} TL</span>
              </div>
            )}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <input type="checkbox" id="applyDiscount" checked={applyDiscount}
                  onChange={(e) => { setApplyDiscount(e.target.checked); if (!e.target.checked) setDiscountAmount(0); }}
                  className="h-4 w-4 text-blue-500 focus:ring-blue-500 border-gray-300 rounded" />
                <label htmlFor="applyDiscount" className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <Percent className="h-4 w-4 text-blue-500" />
                  {t('billModal.applyDiscount')}
                </label>
              </div>
              {applyDiscount && (
                <input type="number" placeholder={t('billModal.discountPlaceholder')} value={discountAmount}
                  onChange={(e) => setDiscountAmount(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />
              )}
            </div>
          </div>

          {/* Sadakat Paneli */}
          {billForm.selectedCustomer && loyaltyConfig && (
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Award className="h-5 w-5 text-purple-600" />
                  <h3 className="text-sm font-semibold text-purple-900">{t('loyalty.title')}</h3>
                </div>
                <div className="flex items-center gap-2">
                  <TierBadge tier={customerLoyalty.tier} size="sm" />
                  <span className="text-sm font-bold text-purple-700">
                    {customerLoyalty.currentPoints.toLocaleString(numLocale)} {t('loyalty.pointsSuffix')}
                  </span>
                </div>
              </div>

              {canRedeem ? (
                <div className="space-y-2 pt-2 border-t border-purple-200">
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="useLoyaltyPoints" checked={useLoyaltyPoints} disabled={isEditing}
                      onChange={(e) => {
                        setUseLoyaltyPoints(e.target.checked);
                        if (!e.target.checked) setPointsToUse(0);
                        else setPointsToUse(Math.min(customerLoyalty.currentPoints, maxUsablePoints));
                      }}
                      className="h-4 w-4 text-purple-500 focus:ring-purple-500 border-gray-300 rounded disabled:opacity-50" />
                    <label htmlFor="useLoyaltyPoints" className="text-sm font-medium text-gray-700 cursor-pointer flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-purple-500" />
                      {t('loyalty.usePoints')}
                    </label>
                    {isEditing && (
                      <span className="text-[11px] text-gray-500 italic ml-auto">{t('loyalty.editingLocked')}</span>
                    )}
                  </div>
                  {useLoyaltyPoints && (
                    <div className="space-y-1.5 pl-6">
                      <div className="flex items-center gap-2">
                        <input type="number" value={pointsToUse} disabled={isEditing}
                          min={minRedeem} max={maxUsablePoints}
                          onChange={(e) => {
                            const val = Math.max(0, Math.floor(Number(e.target.value) || 0));
                            setPointsToUse(Math.min(val, maxUsablePoints));
                          }}
                          className="w-full px-3 py-2 text-sm border border-purple-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white disabled:bg-gray-50 disabled:opacity-60"
                          placeholder={`min ${minRedeem}`} />
                        <span className="text-xs text-gray-500 whitespace-nowrap">/ {maxUsablePoints.toLocaleString(numLocale)}</span>
                      </div>
                      {effectivePointsToUse >= minRedeem && loyaltyDiscount > 0 && (
                        <div className="text-xs text-green-700 font-semibold flex items-center gap-1">
                          {t('loyalty.loyaltyDiscount', { amount: loyaltyDiscount.toLocaleString(numLocale) })}
                        </div>
                      )}
                      {effectivePointsToUse > 0 && effectivePointsToUse < minRedeem && (
                        <div className="text-xs text-red-600">{t('loyalty.minRedeemHint', { min: minRedeem })}</div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-xs text-gray-500 pt-2 border-t border-purple-200">
                  {customerLoyalty.currentPoints < minRedeem
                    ? t('loyalty.notEnoughPoints', { min: minRedeem })
                    : t('loyalty.insufficientRange')}
                </div>
              )}

              {earnPreview && earnPreview.points > 0 && (
                <div className="text-xs text-gray-700 bg-white/70 rounded px-2.5 py-2 flex items-center gap-1.5 border border-purple-100">
                  <Gift className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                  <span>
                    {t('loyalty.earnPreview', { points: earnPreview.points.toLocaleString(numLocale) })}
                    <span className="text-gray-400 ml-1">({earnPreview.tier} × {earnPreview.multiplier})</span>
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Not */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <MessageSquare className="h-4 w-4 text-blue-500" />
              {t('billModal.noteLabel')}
            </label>
            <textarea value={billForm.note || ''} onChange={(e) => onBillFormChange('note', e.target.value)}
              placeholder={t('billModal.notePlaceholder')} rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none text-sm" />
          </div>

          {/* Ödeme Türü */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <CreditCard className="h-4 w-4 text-blue-500" />
              {t('billModal.paymentTypeLabel')}
            </label>
            <select value={billForm.paymentType} onChange={(e) => onBillFormChange('paymentType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all">
              <option value="Nakit">{t('paymentTypes.Nakit')}</option>
              <option value="Kredi Kartı">{t('paymentTypes.Kredi Kartı')}</option>
              <option value="Havale">{t('paymentTypes.Havale')}</option>
              <option value="Açık Hesap">{t('paymentTypes.Açık Hesap')}</option>
              <option value="Açık Hesap Ödeme">{t('paymentTypes.Açık Hesap Ödeme')}</option>
            </select>
          </div>

          {/* Toplam Tutar */}
          <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg p-4 space-y-2">
            {(discountValue > 0 || loyaltyDiscount > 0 || packageDiscount > 0) && (
              <>
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>{t('billModal.subtotalLabel')}</span>
                  <span>{baseTotal.toLocaleString(numLocale)} TL</span>
                </div>
                {discountValue > 0 && (
                  <div className="flex items-center justify-between text-sm text-orange-600">
                    <span className="flex items-center gap-1"><Percent className="h-3.5 w-3.5" /> {t('billModal.discountLine')}</span>
                    <span>-{discountValue.toLocaleString(numLocale)} TL</span>
                  </div>
                )}
                {loyaltyDiscount > 0 && (
                  <div className="flex items-center justify-between text-sm text-purple-600">
                    <span className="flex items-center gap-1">
                      <Sparkles className="h-3.5 w-3.5" />
                      {t('billModal.loyaltyDiscountLine', { points: effectivePointsToUse.toLocaleString(numLocale) })}
                    </span>
                    <span>-{loyaltyDiscount.toLocaleString(numLocale)} TL</span>
                  </div>
                )}
                {packageDiscount > 0 && (
                  <div className="flex items-center justify-between text-sm text-blue-600">
                    <span className="flex items-center gap-1"><Package className="h-3.5 w-3.5" /> Paket indirimi</span>
                    <span>-{packageDiscount.toLocaleString(numLocale)} TL</span>
                  </div>
                )}
                <div className="border-t border-blue-200 pt-2" />
              </>
            )}
            <div className="text-lg font-semibold flex items-center justify-between">
              <span>{t('billModal.totalPayable')}</span>
              <span className={(discountValue > 0 || loyaltyDiscount > 0) ? 'text-green-700' : 'text-gray-900'}>
                {finalTotal.toLocaleString(numLocale)} TL
              </span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end gap-3">
          <button onClick={onClose} disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {t('billModal.cancel')}
          </button>
          <button onClick={onSave} disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 rounded-md transition-all disabled:opacity-70 disabled:cursor-not-allowed">
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            {isSaving ? t('billModal.saving') : (isEditing ? t('billModal.update') : t('billModal.save'))}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default BillModal;
